import { Component, lazy, type ComponentType, type ReactNode } from 'react';

const RELOADED_KEY = 'wt-reloaded-for-update';

/** Recarga la página una sola vez (por sesión del navegador) para traer la versión publicada. */
function reloadOnce(): boolean {
  try {
    if (sessionStorage.getItem(RELOADED_KEY)) return false;
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** Una carga correcta borra la marca, para que la próxima actualización también pueda recargar. */
export function markAppLoaded() {
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(RELOADED_KEY);
    } catch {
      /* sin almacenamiento: no pasa nada */
    }
  }, 10_000);
}

/**
 * Como `lazy`, pero si la parte de la web no se puede descargar (se acaba de publicar una versión nueva y esta pestaña
 * pide la anterior, o el servidor se estaba reiniciando) recarga una vez en lugar de dejar la pantalla en blanco.
 */
export function lazyWithReload<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() => load().catch((err) => (reloadOnce() ? new Promise<{ default: T }>(() => {}) : Promise.reject(err))));
}

/** Si algo falla al pintar la web, se explica y se ofrece recargar, nunca una pantalla en blanco. */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-center">
        <div className="max-w-sm">
          <h1 className="text-lg font-semibold text-slate-900">No se ha podido cargar esta pantalla</h1>
          <p className="mt-2 text-sm text-slate-600">Puede que acabemos de publicar una versión nueva de WulfTime. Recarga la página para continuar; tus fichajes no se pierden.</p>
          <button type="button" onClick={() => window.location.reload()} className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            Recargar
          </button>
        </div>
      </div>
    );
  }
}
